import type { CmmdForgeRuntimeApiConfig } from "../environments/primary/cmmdForgeRuntimeApi";
import { CmmdForgeRuntimeApiClient } from "../environments/primary/cmmdForgeRuntimeApi";
import {
  readCloudProjectCatalog,
  type CloudProjectCatalog,
} from "../environments/primary/cloudProjectCatalog";

const SSE_AUTH_REQUIRED_EVENT = "auth-required";
const SSE_CLOUD_PROJECTS_EVENT = "cloud-projects";

export type CloudCatalogRequestResult =
  | { readonly kind: "catalog"; readonly catalog: CloudProjectCatalog }
  | { readonly kind: "timed-out" }
  | { readonly kind: "failed"; readonly error: unknown };

export async function requestCloudProjectCatalog(
  config: CmmdForgeRuntimeApiConfig,
  controller: AbortController,
  timeoutMs: number,
): Promise<CloudCatalogRequestResult> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const client = new CmmdForgeRuntimeApiClient(config);
  const request = client
    .listCloudProjects({ signal: controller.signal })
    .then((catalog) => ({ kind: "catalog", catalog }) as const)
    .catch((error: unknown) => ({ kind: "failed", error }) as const);
  const timeout = new Promise<CloudCatalogRequestResult>((resolve) => {
    timeoutId = setTimeout(() => {
      controller.abort();
      resolve({ kind: "timed-out" });
    }, timeoutMs);
  });

  try {
    return await Promise.race([request, timeout]);
  } finally {
    if (timeoutId !== null) clearTimeout(timeoutId);
  }
}

export function createCloudCatalogAuthHeaders(
  config: CmmdForgeRuntimeApiConfig,
): Record<string, string> {
  const bearer = config.accessToken ?? config.resolveAccessToken?.();
  const headers: Record<string, string> = { Accept: "text/event-stream" };
  if (bearer) headers.Authorization = `Bearer ${bearer}`;
  return headers;
}

export function resolveCloudCatalogEventsUrl(baseUrl: string, path: string): string {
  const url = new URL(baseUrl);
  url.pathname = path;
  url.search = "";
  url.hash = "";
  return url.toString();
}

export async function prepareSseReader(
  response: Response,
): Promise<ReadableStreamDefaultReader<Uint8Array> | "auth-required" | "fallback"> {
  if (response.status === 401 || response.status === 403) {
    const hasCode = await hasCmmdAuthRequiredCode(response);
    if (hasCode || response.status === 401) return "auth-required";
  }
  if (response.status === 404 || response.status === 405 || !response.ok || !response.body) {
    return "fallback";
  }
  return response.body.getReader();
}

export async function consumeSseFrames({
  reader,
  signal,
  onAuthRequired,
  onCatalog,
}: {
  readonly reader: ReadableStreamDefaultReader<Uint8Array>;
  readonly signal: AbortSignal;
  readonly onAuthRequired: () => void;
  readonly onCatalog: (catalog: CloudProjectCatalog) => void;
}): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (signal.aborted) return;
      if (done) return;
      const chunk = readSseChunk(buffer, decoder.decode(value, { stream: true }));
      buffer = chunk.buffer;
      const shouldStop = chunk.messages.some((message) =>
        applySseMessage(message, onAuthRequired, onCatalog),
      );
      if (shouldStop) return;
    }
  } finally {
    reader.releaseLock();
  }
}

function readSseChunk(
  buffer: string,
  value: string,
): { readonly buffer: string; readonly messages: readonly string[] } {
  const messages = `${buffer}${value}`.split(/\n\n/);
  return { buffer: messages.pop() ?? "", messages };
}

function applySseMessage(
  message: string,
  onAuthRequired: () => void,
  onCatalog: (catalog: CloudProjectCatalog) => void,
): boolean {
  const event = readSseEvent(message);
  if (event.name === SSE_AUTH_REQUIRED_EVENT) {
    onAuthRequired();
    return true;
  }
  const catalog =
    event.name === SSE_CLOUD_PROJECTS_EVENT ? readSseCloudProjectCatalog(event.data) : null;
  if (catalog) onCatalog(catalog);
  return false;
}

async function hasCmmdAuthRequiredCode(response: Response): Promise<boolean> {
  try {
    const body = (await response.clone().json()) as Record<string, unknown> | null;
    return typeof body?.code === "string" && body.code === "CMMD_AUTH_REQUIRED";
  } catch {
    return false;
  }
}

function readSseCloudProjectCatalog(data: string): CloudProjectCatalog | null {
  try {
    return readCloudProjectCatalog(JSON.parse(data) as unknown);
  } catch {
    return null;
  }
}

function readSseEvent(message: string): { readonly name: string; readonly data: string } {
  let name = "";
  let data = "";
  for (const line of message.split("\n")) {
    if (line.startsWith("event: ")) name = line.slice(7).trim();
    if (line.startsWith("data: ")) data = line.slice(6).trim();
  }
  return { name, data };
}
